import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  motion, AnimatePresence,
  useScroll, useTransform, useMotionValue, useSpring, useInView,
} from "framer-motion";
import {
  Buildings, ChatCircle, Lightning, CheckCircle, CheckSquare,
  ArrowRight, Sparkle, Plugs, Rocket, Crown,
  InstagramLogo, FacebookLogo, TrendUp, Receipt, MapPin, X,
  WhatsappLogo, House, Scales, Storefront, LinkSimple, UsersThree, Share, List,
} from "@phosphor-icons/react";
import logo from "@/assets/logo.png";

// ─── Data ─────────────────────────────────────────────────────────────────────

const TICKER = [
  "Auto iCal Sync","Channel Manager","AI Dynamic Pricing","GST-Ready Reports","Guest Automations",
  "Smart Inbox","Task Board","ROI Evaluator","Booking Calendar","P&L Exports",
  "Guest Guidebook","WhatsApp Alerts","Setup Advisor","Legal Resource Hub","Direct Booking","Team Access",
];

const FEATURES = [
  { icon: Lightning,    title: "AI Dynamic Pricing",     desc: "Our AI analyses local demand, seasonal trends and your occupancy to suggest the perfect nightly rate. Hosts typically see 15–25% more revenue in the first month.",   accent: "amber",  wide: true,  stat: "+22% avg revenue" },
  { icon: Share,        title: "Channel Manager",         desc: "Push your rates and availability to 20+ OTAs in real time. One rate change in HostFolio updates Airbnb, Booking.com, Agoda, MakeMyTrip, Expedia and more — instantly.",  accent: "violet", stat: "20+ OTAs" },
  { icon: ChatCircle,   title: "Smart Guest Inbox",       desc: "AI drafts replies in warm, professional or brief tones across all properties. Send in one click.",                                                                       accent: "violet" },
  { icon: Receipt,      title: "GST-Ready P&L Reports",  desc: "Every rupee tracked. CGST, SGST and IGST auto-split based on guest location. Export a clean PDF for your CA in one click.",                                             accent: "green",  wide: true,  stat: "CGST · SGST · IGST" },
  { icon: House,        title: "Guest Guidebook",         desc: "Auto-send a digital guidebook to guests on booking — WiFi, check-in steps, local restaurants, nearby attractions and emergency contacts. All in one link.",             accent: "teal" },
  { icon: WhatsappLogo, title: "WhatsApp Automations",    desc: "5 triggers built-in: 48h before check-in, day-of check-in instructions, checkout reminder, post-stay review request, and cleaning alert to staff. All configurable.",  accent: "green" },
  { icon: CheckSquare,  title: "Task Board",              desc: "After every checkout, a cleaning task is auto-created and your staff gets a WhatsApp alert. Cleaners upload proof photos on completion — no manual follow-up needed.", accent: "rose" },
  { icon: UsersThree,   title: "Team Access",             desc: "Add co-hosts, managers and maintenance staff as separate logins. Each role sees only their assigned properties and permitted actions — no shared passwords.",          accent: "blue" },
  { icon: Storefront,   title: "Setup Advisor",           desc: "40-item curated checklist to set up your rental the right way — with INR cost estimates, priority tags and affiliate links to buy each item.",                        accent: "amber", link: "/setup-advisor" },
  { icon: Scales,       title: "Legal Resource Hub",      desc: "Plain-English guides on GST registration, police verification, rental agreements and damage recovery. Download a ready-to-use rental agreement PDF.",                  accent: "violet", link: "/legal" },
  { icon: TrendUp,      title: "Property ROI Evaluator",  desc: "AI-powered calculator for purchase, rental and lease scenarios. Know your returns before you invest.",                                                                 accent: "blue" },
  { icon: LinkSimple,   title: "Direct Booking Link",     desc: "Share a personal booking page with guests. They pay via Razorpay directly — zero OTA commission, full payment to you.",                                                accent: "amber", link: "/properties" },
  { icon: Plugs,        title: "Auto iCal Sync",          desc: "Paste your iCal link from Airbnb, Booking.com, MakeMyTrip, Goibibo, VRBO or Agoda once. Every booking syncs every 15 minutes — zero manual entry.",                    accent: "blue",  stat: "6 platforms" },
];

const A = {
  amber:  { card: "bg-gradient-to-br from-amber-50 to-white border-amber-200",   icon: "bg-amber-100  text-amber-600",  stat: "bg-amber-400  text-white" },
  blue:   { card: "bg-gradient-to-br from-blue-50  to-white border-blue-200",    icon: "bg-blue-100   text-blue-600",   stat: "bg-blue-500   text-white" },
  violet: { card: "bg-gradient-to-br from-violet-50 to-white border-violet-200", icon: "bg-violet-100 text-violet-600", stat: "bg-violet-500 text-white" },
  green:  { card: "bg-gradient-to-br from-emerald-50 to-white border-emerald-200",icon: "bg-emerald-100 text-emerald-600",stat:"bg-emerald-500 text-white" },
  rose:   { card: "bg-gradient-to-br from-rose-50  to-white border-rose-200",    icon: "bg-rose-100   text-rose-500",   stat: "bg-rose-500   text-white" },
  teal:   { card: "bg-gradient-to-br from-teal-50  to-white border-teal-200",    icon: "bg-teal-100   text-teal-600",   stat: "bg-teal-500   text-white" },
};

const PLANS = [
  {
    id: "free", name: "Free", price: 1149, icon: Sparkle, trial: true,
    tagline: "Free for 1 month, then ₹1,149/mo",
    features: ["1 property · 1 iCal feed","20 AI smart replies/mo","Booking calendar & task board","GST-ready P&L reports","Guest Guidebook","Setup Advisor & Legal Hub"],
  },
  {
    id: "starter", name: "Starter", price: 3449, icon: Rocket,
    tagline: "AI pricing that pays for itself",
    features: ["3 properties · iCal sync + channel manager","200 AI smart replies/mo","AI Dynamic Pricing","Channel manager (push rates to 20+ OTAs)","Direct booking link (zero OTA commission)","WhatsApp automations","Guest Guidebook","Property ROI Evaluator"],
  },
  {
    id: "pro", name: "Pro", price: 11449, icon: Crown, popular: true,
    tagline: "Run a real operation",
    features: ["10 properties · iCal sync + channel manager","1,000 AI smart replies/mo","Team access (co-host, manager & maintenance)","Automations & WhatsApp task alerts",{ text:"Custom guidebook branding", soon:true },"Priority support","Everything in Starter"],
  },
  {
    id: "agency", name: "Agency", price: null, icon: Buildings, contact: true,
    tagline: "Manage properties for multiple owners",
    features: ["Unlimited properties · iCal sync + channel manager","Unlimited AI smart replies","White-label branding","Dedicated account manager","Consolidated multi-property P&L","Everything in Pro"],
  },
];

// ─── Animation helpers ─────────────────────────────────────────────────────────

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const fadeUp  = { hidden: { opacity: 0, y: 32 }, show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.34, 1.1, 0.64, 1] } } };

// ─── Count-up stat ────────────────────────────────────────────────────────────

function StatCounter({ prefix = "", end, suffix = "", label, sub }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  useEffect(() => {
    if (!inView || end === 0) { setCount(end); return; }
    const dur = 1600;
    const t0 = Date.now();
    const id = setInterval(() => {
      const p = Math.min((Date.now() - t0) / dur, 1);
      setCount(Math.round((1 - Math.pow(1 - p, 3)) * end));
      if (p >= 1) clearInterval(id);
    }, 16);
    return () => clearInterval(id);
  }, [inView, end]);

  return (
    <div ref={ref} className="text-center">
      <p className="font-manrope text-5xl md:text-6xl font-bold text-slate-900 tracking-tight">{prefix}{count}{suffix}</p>
      <p className="text-sm font-semibold text-slate-600 mt-2">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Marquee ──────────────────────────────────────────────────────────────────

function Marquee() {
  const items = [...TICKER, ...TICKER];
  return (
    <div className="overflow-hidden bg-amber-50 py-3.5 select-none border-y border-amber-100">
      <motion.div animate={{ x: ["0%", "-50%"] }} transition={{ duration: 32, repeat: Infinity, ease: "linear" }}
        className="flex gap-10 whitespace-nowrap w-max">
        {items.map((item, i) => (
          <span key={i} className="flex items-center gap-2.5 text-[11px] font-bold text-amber-700 uppercase tracking-widest">
            <span className="w-1 h-1 rounded-full bg-amber-400 shrink-0" />{item}
          </span>
        ))}
      </motion.div>
    </div>
  );
}

// ─── Glow CTA ────────────────────────────────────────────────────────────────

function GlowCTA({ onClick, children, className = "" }) {
  return (
    <motion.button onClick={onClick} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
      animate={{ boxShadow: ["0 0 0 0 rgba(251,191,36,0.45)", "0 0 0 14px rgba(251,191,36,0)", "0 0 0 0 rgba(251,191,36,0)"] }}
      transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
      className={`inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold rounded-full px-8 py-4 text-base transition-colors shadow-lg shadow-amber-200/50 ${className}`}>
      {children}
    </motion.button>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function LandingV2() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [contactCopied, setContactCopied] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");

  const { scrollY } = useScroll();
  const navBg     = useTransform(scrollY, [0, 80], ["rgba(255,255,255,0.0)", "rgba(255,255,255,0.97)"]);
  const navBorder = useTransform(scrollY, [0, 80], ["rgba(226,232,240,0)", "rgba(226,232,240,1)"]);
  const blob1Y    = useTransform(scrollY, [0, 800], [0, -120]);
  const dashY     = useTransform(scrollY, [0, 600], [0, -40]);

  const rawX = useMotionValue(-800);
  const rawY = useMotionValue(-800);
  const glowX = useSpring(rawX, { stiffness: 55, damping: 18 });
  const glowY = useSpring(rawY, { stiffness: 55, damping: 18 });

  useEffect(() => {
    const fn = (e) => { rawX.set(e.clientX); rawY.set(e.clientY); };
    window.addEventListener("mousemove", fn);
    return () => window.removeEventListener("mousemove", fn);
  }, [rawX, rawY]);

  useEffect(() => {
    const ids = ["hero", "how-it-works", "features", "pricing", "faq"];
    const obs = new IntersectionObserver(
      (entries) => { const hit = entries.find(e => e.isIntersecting); if (hit) setActiveSection(hit.target.id); },
      { threshold: 0.3, rootMargin: "-60px 0px 0px 0px" }
    );
    ids.forEach(id => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  const NAV = [
    ["How it works", "#how-it-works", "how-it-works"],
    ["Features",     "#features",     "features"],
    ["Pricing",      "#pricing",      "pricing"],
    ["Blog",         "/blog",         ""],
    ["FAQ",          "#faq",          "faq"],
  ];

  return (
    <div className="min-h-screen bg-white font-outfit text-slate-800 overflow-x-hidden">

      {/* Cursor ambient glow */}
      <div className="fixed inset-0 pointer-events-none z-0 hidden lg:block">
        <motion.div className="absolute w-[700px] h-[700px] rounded-full"
          style={{ x: glowX, y: glowY, translateX: "-50%", translateY: "-50%",
            background: "radial-gradient(circle, rgba(251,191,36,0.06) 0%, transparent 65%)" }} />
      </div>

      {/* ── Nav ── */}
      <motion.header style={{ backgroundColor: navBg, borderBottomColor: navBorder }}
        className="fixed top-0 left-0 right-0 z-50 border-b h-16 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">

          <div className="font-manrope text-xl font-bold shrink-0">
            <span className="text-slate-900">Host</span><span className="text-amber-500">Folio</span>
          </div>

          <nav className="hidden md:flex items-center bg-black/[0.04] rounded-full px-1.5 py-1.5 gap-0.5">
            {NAV.map(([label, href, id]) => {
              const isActive = id !== "" && activeSection === id;
              return (
                <a key={label} href={href}
                  className={`text-sm font-semibold px-4 py-1.5 rounded-full transition-all duration-200 ${
                    isActive
                      ? "bg-white text-slate-900 shadow-sm shadow-black/10"
                      : "text-slate-500 hover:text-slate-800 hover:bg-white/60"
                  }`}>
                  {label}
                </a>
              );
            })}
          </nav>

          <div className="hidden md:flex items-center gap-2 shrink-0">
            <button onClick={() => navigate("/login")}
              className="text-sm font-semibold text-slate-600 hover:text-slate-900 px-4 py-2 rounded-full hover:bg-black/[0.04] transition">
              Sign in
            </button>
            <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => navigate("/login")}
              className="bg-slate-900 hover:bg-slate-700 text-white text-sm font-bold px-5 py-2.5 rounded-full transition shadow-sm">
              Get Started Free
            </motion.button>
          </div>

          <button onClick={() => setMobileOpen(true)} className="md:hidden p-2 rounded-xl hover:bg-slate-100 transition">
            <List size={22} weight="bold" className="text-slate-700" />
          </button>
        </div>
      </motion.header>

      {/* ── Mobile overlay ── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-white flex flex-col">
            <div className="flex items-center justify-between px-6 h-16 border-b border-slate-100">
              <div className="font-manrope text-xl font-bold">
                <span className="text-slate-900">Host</span><span className="text-amber-500">Folio</span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-2 rounded-xl hover:bg-slate-100 transition">
                <X size={22} className="text-slate-600" />
              </button>
            </div>
            <motion.nav initial="hidden" animate="show" variants={stagger} className="flex flex-col gap-1 p-6 flex-1">
              {NAV.map(([label, href]) => (
                <motion.a key={label} href={href} variants={fadeUp} onClick={() => setMobileOpen(false)}
                  className="text-2xl font-bold text-slate-800 hover:text-amber-500 py-3 border-b border-slate-100 transition-colors">
                  {label}
                </motion.a>
              ))}
              <motion.div variants={fadeUp} className="flex flex-col gap-3 mt-8">
                <button onClick={() => { navigate("/login"); setMobileOpen(false); }}
                  className="w-full py-4 rounded-2xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition">
                  Sign in
                </button>
                <button onClick={() => { navigate("/login"); setMobileOpen(false); }}
                  className="w-full py-4 rounded-2xl bg-amber-400 text-slate-900 font-bold hover:bg-amber-300 transition">
                  Get Started Free
                </button>
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Hero ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <section id="hero" className="relative bg-white overflow-hidden">

        <div className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, rgba(15,23,42,0.055) 1px, transparent 1px)", backgroundSize: "34px 34px" }} />

        <motion.div className="absolute -top-48 -right-48 w-[1000px] h-[1000px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(251,191,36,0.10) 0%, transparent 60%)", y: blob1Y }}
          animate={{ scale: [1, 1.04, 1] }} transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }} />

        <div className="relative max-w-7xl mx-auto px-6 w-full pt-24 md:pt-32 pb-12">
          <motion.div initial="hidden" animate="show" variants={stagger} className="max-w-4xl">

            <motion.div variants={fadeUp}
              className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-bold px-3.5 py-1.5 rounded-full mb-8 tracking-wide">
              <Sparkle size={11} weight="fill" /> Built for Indian Airbnb hosts
            </motion.div>

            <h1 className="font-manrope font-bold text-slate-900 tracking-tight mb-8"
              style={{ fontSize: "clamp(3rem, 7.5vw, 6.5rem)", lineHeight: 0.93 }}>
              <motion.span variants={fadeUp} className="block">Stop juggling</motion.span>
              <motion.span variants={fadeUp} className="block">
                <span className="relative inline-block">
                  <span className="relative z-10 text-amber-500">spreadsheets.</span>
                  <motion.span
                    initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
                    transition={{ delay: 0.9, duration: 0.6, ease: [0.34, 1.1, 0.64, 1] }}
                    className="absolute bottom-1 left-0 right-0 h-3 bg-amber-100 rounded-full -z-0 origin-left" />
                </span>
              </motion.span>
            </h1>

            <motion.p variants={fadeUp} className="text-lg md:text-xl text-slate-500 max-w-xl leading-relaxed mb-10">
              The all-in-one dashboard for Airbnb hosts in India. Booking sync, AI pricing, GST-ready P&amp;L and guest automation — in one place.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-start gap-4 mb-6">
              <GlowCTA onClick={() => navigate("/login")}>
                Start Free — 1 Month on Us <ArrowRight size={17} weight="bold" />
              </GlowCTA>
              <a href="#how-it-works"
                className="inline-flex items-center gap-2 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold rounded-full px-8 py-4 text-base transition-all">
                See How It Works
              </a>
            </motion.div>

            <motion.p variants={fadeUp} className="text-xs text-slate-500 mb-6">
              No credit card required &middot; 2-minute setup &middot; Cancel anytime
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap gap-2">
              {["Goa","Coorg","Manali","Udaipur","Shimla","Rishikesh","Kerala","Mumbai"].map(city => (
                <span key={city} className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-full font-medium">
                  <MapPin size={9} weight="fill" className="text-amber-400" />{city}
                </span>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Dashboard preview */}
        <motion.div style={{ y: dashY }}
          initial={{ opacity: 0, y: 90 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 1.1, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="relative max-w-6xl mx-auto px-6 w-full">
          <div className="rounded-t-2xl border border-b-0 border-slate-200/80 overflow-hidden"
            style={{
              boxShadow: "0 -16px 60px rgba(0,0,0,0.07), 0 -4px 16px rgba(0,0,0,0.04)",
              transform: "perspective(1600px) rotateX(3.5deg)",
              transformOrigin: "bottom center",
            }}>
            <div className="bg-slate-50 border-b border-slate-100 px-5 py-3.5 flex items-center gap-3">
              <div className="flex gap-1.5 shrink-0">
                <div className="w-3 h-3 rounded-full bg-red-400/70" />
                <div className="w-3 h-3 rounded-full bg-amber-400/70" />
                <div className="w-3 h-3 rounded-full bg-green-400/70" />
              </div>
              <div className="flex-1 h-6 bg-white border border-slate-200 rounded-md text-[11px] text-slate-400 flex items-center px-3 max-w-xs">
                app.hostfolio.in/dashboard
              </div>
            </div>
            <div className="bg-white p-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {[
                  ["Monthly Revenue", "₹1,24,500", "text-amber-500", "+18%"],
                  ["Occupancy",       "73%",        "text-green-600", "+5%"],
                  ["Avg Daily Rate",  "₹3,200",     "text-blue-500",  "Optimal"],
                  ["Net Profit",      "₹68,200",    "text-green-600", "+12%"],
                ].map(([l, v, c, badge]) => (
                  <div key={l} className="bg-slate-50 rounded-xl p-3.5 border border-slate-100">
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2">{l}</p>
                    <div className="flex items-end justify-between gap-1">
                      <p className={`text-base font-bold leading-none ${c}`}>{v}</p>
                      <span className="text-[9px] font-bold text-green-600 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded-full shrink-0">{badge}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2 bg-slate-50 border border-slate-100 rounded-xl h-28 flex items-end gap-1 px-3 pb-3 pt-8 relative overflow-hidden">
                  <p className="absolute top-3 left-3 text-[9px] font-bold text-slate-400 uppercase tracking-wide">Revenue · Last 12 months</p>
                  {[42, 58, 49, 72, 61, 88, 52, 77, 48, 82, 68, 96].map((h, i) => (
                    <div key={i} className="flex-1 rounded-t"
                      style={{ height: `${h}%`, background: i === 11 ? "linear-gradient(to top,#F59E0B,#FDE68A)" : "#E2E8F0" }} />
                  ))}
                </div>
                <div className="space-y-2">
                  {[["Goa Beach Villa","Airbnb","₹8,400","text-orange-500"],["Manali Retreat","Booking.com","₹14,200","text-blue-600"],["Coorg Cottage","Direct","₹6,800","text-green-600"]].map(([p, s, r, sc]) => (
                    <div key={p} className="bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold text-slate-800 truncate">{p}</p>
                        <p className={`text-[9px] font-medium ${sc}`}>{s}</p>
                      </div>
                      <span className="text-[10px] font-bold text-slate-700 shrink-0">{r}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2 }}
          className="flex flex-col items-center gap-2 py-8 bg-white relative z-10">
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.22em]">Scroll to explore</p>
          <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.7, repeat: Infinity }}
            className="w-5 h-5 rounded-full border-2 border-slate-300 flex items-center justify-center">
            <div className="w-1 h-1 rounded-full bg-slate-400" />
          </motion.div>
        </motion.div>
      </section>

      {/* ── Ticker ── */}
      <Marquee />

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Integration logos ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-10 bg-slate-50 border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6">iCal sync — pull bookings automatically</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {[
                { name: "Airbnb",      color: "#FF5A5F" },
                { name: "Booking.com", color: "#003580" },
                { name: "MakeMyTrip",  color: "#E8272B" },
                { name: "Goibibo",     color: "#E8272B" },
                { name: "VRBO",        color: "#1D5EFF" },
                { name: "Agoda",       color: "#DF1B12" },
              ].map(({ name, color }) => (
                <div key={name} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl shadow-sm">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                  <span className="text-sm font-bold text-slate-700">{name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">+</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <div className="text-center">
            <p className="text-xs font-bold text-violet-500 uppercase tracking-widest mb-6">Channel Manager — push rates &amp; availability in real time</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {[
                { name: "Airbnb",      color: "#FF5A5F" },
                { name: "Booking.com", color: "#003580" },
                { name: "Agoda",       color: "#DF1B12" },
                { name: "MakeMyTrip",  color: "#E8272B" },
                { name: "Expedia",     color: "#00355F" },
                { name: "Goibibo",     color: "#E8272B" },
                { name: "20+ more",    color: "#7C3AED" },
              ].map(({ name, color }) => (
                <div key={name} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-violet-100 rounded-xl shadow-sm">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                  <span className="text-sm font-bold text-slate-700">{name}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-4">Rates update across all OTAs the moment you change them in HostFolio</p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Stats ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-10 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-6 text-center">
            {[
              ["1 month free", "No credit card needed"],
              ["2 min setup",   "Paste iCal & go"],
              ["India-first",   "GST built in"],
              ["₹0 hidden fees","No surprise charges"],
            ].map(([n, l]) => (
              <div key={n}>
                <p className="font-manrope text-xl md:text-2xl font-bold text-slate-900">{n}</p>
                <p className="text-xs text-slate-500 mt-1">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── City presence ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-6 border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Hosts across India are using HostFolio</p>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2.5">
            {["Goa","Bangalore","Mumbai","Manali","Coorg","Udaipur","Shimla","Rishikesh","Kerala","Lonavala","Ooty","Kasauli"].map(city => (
              <span key={city} className="text-sm font-semibold text-slate-600 inline-flex items-center gap-1.5">
                <MapPin size={10} weight="fill" className="text-amber-400" />{city}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── How it works ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <section id="how-it-works" className="py-14 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-8 md:mb-14">
            <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="text-xs font-bold text-amber-500 uppercase tracking-widest mb-4">
              Simple setup
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: 0.06, duration: 0.65, ease: [0.34, 1.1, 0.64, 1] }}
              className="font-manrope font-bold text-slate-900 leading-[0.95]"
              style={{ fontSize: "clamp(2.8rem, 5.5vw, 5rem)" }}>
              Up and running<br />in 2 minutes.
            </motion.h2>
            <p className="text-slate-500 max-w-lg mt-4">No complex setup. No developer needed. Just paste your Airbnb link and go.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 border border-slate-100 rounded-3xl overflow-hidden"
            style={{ boxShadow: "0 4px 32px rgba(0,0,0,0.05)" }}>
            {[
              { n: "01", t: "Connect your listings",  d: "Paste your iCal link from Airbnb or Booking.com. Done in 30 seconds — no developer, no API key needed.", tag: "30 seconds" },
              { n: "02", t: "Everything syncs live",  d: "Bookings, revenue and occupancy update automatically every 15 minutes across all platforms. Always accurate.", tag: "Auto-sync" },
              { n: "03", t: "Grow on autopilot",      d: "AI pricing, GST reports, automated guest messages and task boards run themselves while you host.", tag: "Hands-free" },
            ].map(({ n, t, d, tag }, i) => (
              <motion.div key={n}
                initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.12, duration: 0.65, ease: [0.34, 1.1, 0.64, 1] }}
                className={`bg-white p-10 md:p-12 flex flex-col gap-6 hover:bg-slate-50/70 transition-colors
                  ${i < 2 ? "md:border-r border-slate-100" : ""}
                  ${i > 0 ? "border-t md:border-t-0 border-slate-100" : ""}`}>
                <div className="flex items-start justify-between">
                  <span className="font-manrope text-7xl font-bold text-slate-200 leading-none">{n}</span>
                  <span className="text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-100 px-2.5 py-1 rounded-full shrink-0 mt-2">{tag}</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-xl text-slate-900 mb-3">{t}</h3>
                  <p className="text-slate-500 leading-relaxed">{d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Features ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <section id="features" className="py-14 md:py-24 border-t border-slate-100" style={{ background: "#F8F9FA" }}>
        <div className="max-w-7xl mx-auto px-6">

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 mb-16">
            <div>
              <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="text-xs font-bold text-amber-500 uppercase tracking-widest mb-4">
                Everything you need
              </motion.p>
              <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: 0.06, duration: 0.65 }}
                className="font-manrope font-bold text-slate-900 leading-[0.95]"
                style={{ fontSize: "clamp(2.8rem, 5.5vw, 5rem)" }}>
                One dashboard.<br />Zero chaos.
              </motion.h2>
            </div>
            <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="text-slate-500 text-lg leading-relaxed max-w-sm lg:mb-2">
              Stop juggling spreadsheets, WhatsApp groups and 5 different apps. HostFolio handles it all.
            </motion.p>
          </div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {FEATURES.map(({ icon: Icon, title, desc, accent, wide, stat, link }) => {
              const a = A[accent];
              return (
                <motion.div key={title} variants={fadeUp} whileHover={{ y: -4 }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                whileHover={{ y: -6, boxShadow: "0 16px 40px rgba(0,0,0,0.12)", transition: { duration: 0.2 } }}
                  className={`border rounded-3xl p-6 flex flex-col gap-4 ${a.card} ${wide ? "md:col-span-2" : ""}`}
                  style={{ boxShadow: "0 2px 16px rgba(0,0,0,0.07)" }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-sm ${a.icon}`}>
                      <Icon size={24} weight="duotone" />
                    </div>
                    {stat && <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full shrink-0 ${a.stat}`}>{stat}</span>}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-900 text-base mb-1.5">{title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
                  </div>
                  {link && (
                    <a href={link} className="text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors flex items-center gap-1 mt-auto">
                      Explore <ArrowRight size={11} weight="bold" />
                    </a>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Comparison table ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-14 md:py-24 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-10 md:mb-14">
            <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="text-xs font-bold text-amber-500 uppercase tracking-widest mb-4">
              The difference
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: 0.06, duration: 0.65 }}
              className="font-manrope font-bold text-slate-900 leading-[0.95]"
              style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
              Before HostFolio<br />vs. After.
            </motion.h2>
            <p className="text-slate-500 text-sm mt-4">Short-term rental management in India, the way it should be.</p>
          </div>

          <div className="overflow-x-auto -mx-2 px-2">
            <div className="rounded-3xl overflow-hidden border border-slate-200 min-w-[560px]" style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
              <div className="grid grid-cols-3 bg-slate-900 text-white text-xs font-bold uppercase tracking-wide">
                <div className="px-5 py-3.5 text-slate-400">Task</div>
                <div className="px-5 py-3.5 text-red-400 flex items-center gap-2"><X size={12} weight="bold" /> Before</div>
                <div className="px-5 py-3.5 text-emerald-400 flex items-center gap-2"><CheckCircle size={12} weight="fill" /> After HostFolio</div>
              </div>
              {[
                ["Booking sync",         "Manual copy every day",                       "Instant via channel manager"],
                ["GST filing",           "CA asks, you panic",                          "P&L PDF in one click"],
                ["Cleaner notification", "WhatsApp, manually forwarded",                "Auto-sent on every checkout"],
                ["Nightly rate",         "Gut feel or spreadsheet",                     "AI suggestion on live market data"],
                ["Guest questions",      "Phone call at midnight",                      "Guidebook auto-sent on booking"],
                ["Cost",                 "Airbnb fees + CA fees + 4 app subscriptions", "₹1,149/month all-in"],
              ].map(([task, before, after], i) => (
                <div key={task} className={`grid grid-cols-3 text-sm ${i % 2 === 0 ? "bg-white" : "bg-slate-50"} border-t border-slate-100`}>
                  <div className="px-5 py-4 font-semibold text-slate-700">{task}</div>
                  <div className="px-5 py-4 text-slate-500">{before}</div>
                  <div className="px-5 py-4 text-emerald-700 font-medium">{after}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Problem / Solution ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-14 md:py-24 border-t border-slate-100" style={{ background: "#F8F9FA" }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-8 md:mb-12">
            <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="text-xs font-bold text-amber-500 uppercase tracking-widest mb-4">
              Sound familiar?
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: 0.06, duration: 0.65 }}
              className="font-manrope font-bold text-slate-900 leading-[0.95]"
              style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
              Every Indian host<br />faces the same chaos.
            </motion.h2>
            <p className="text-slate-500 mt-4">HostFolio is built to fix exactly this.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                pain: "Copying bookings manually from Airbnb and Booking.com into a spreadsheet every day",
                fix:  "Real-time two-way sync via channel manager — keeps Airbnb, Booking.com, MakeMyTrip, Goibibo, VRBO and Agoda in sync to prevent double bookings, with iCal feeds for any platform not on the channel manager.",
              },
              {
                pain: "Sending the same check-in instructions to every guest on WhatsApp by hand",
                fix:  "WhatsApp automations fire the moment a booking is confirmed — guidebook link, WiFi, check-in steps.",
              },
              {
                pain: "Scrambling for receipts at CA time, unsure what the GST liability is",
                fix:  "Every rupee tracked. P&L PDF in one click, GST-ready with CGST/SGST/IGST split.",
              },
              {
                pain: "Guests calling at midnight asking for WiFi password, restaurant suggestions and directions",
                fix:  "Guest Guidebook auto-sent on booking. One link answers every question before they even arrive.",
              },
            ].map(({ pain, fix }, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.55 }}
                className="rounded-3xl border border-slate-100 bg-white overflow-hidden"
                style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                <div className="bg-red-50 border-b border-red-100 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                      <X size={10} weight="bold" className="text-red-500" />
                    </div>
                    <p className="text-[10px] font-bold text-red-500 uppercase tracking-wide">The problem</p>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">&ldquo;{pain}&rdquo;</p>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                      <CheckCircle size={12} weight="fill" className="text-emerald-500" />
                    </div>
                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wide">HostFolio fix</p>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed font-semibold">{fix}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Who it's for ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-20 bg-amber-50 border-y border-amber-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-8 md:mb-12">
            <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="text-xs font-bold text-amber-500 uppercase tracking-widest mb-4">
              Who it&rsquo;s for
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: 0.06, duration: 0.65 }}
              className="font-manrope font-bold text-slate-900 leading-[0.95]"
              style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
              Built for every type<br />of Indian host.
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { type: "Single property host",     use: "iCal sync, guest inbox and GST reports — everything you need for one property, nothing you don't." },
              { type: "Multi-property host",       use: "Unified calendar, task board and AI pricing across all properties from one dashboard." },
              { type: "New host setting up",       use: "Setup Advisor walks you through every item to buy. Legal Hub explains GST, police verification and rental agreements." },
              { type: "Host with a CA",            use: "Monthly P&L PDF export with GST split and TDS tracking — hand it straight to your accountant." },
              { type: "Weekend / part-time host",  use: "Guest Guidebook auto-sends so guests don't interrupt your day job with WiFi or directions questions." },
              { type: "Airbnb Superhost",          use: "AI pricing, smart inbox and automated tasks help you maintain high ratings across all properties at scale." },
            ].map(({ type, use }, i) => (
              <motion.div key={type}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}
                className="bg-white rounded-2xl border border-amber-100 p-5"
                style={{ boxShadow: "0 2px 12px rgba(245,158,11,0.08)" }}>
                <p className="font-bold text-slate-900 text-sm mb-2">{type}</p>
                <p className="text-xs text-slate-600 leading-relaxed">{use}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Pricing ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <section id="pricing" className="py-14 md:py-24 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6">

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
            <div>
              <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="text-xs font-bold text-amber-500 uppercase tracking-widest mb-4">
                Simple pricing
              </motion.p>
              <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: 0.06, duration: 0.65 }}
                className="font-manrope font-bold text-slate-900 leading-[0.95]"
                style={{ fontSize: "clamp(2.8rem, 5.5vw, 5rem)" }}>
                Start free.<br />Scale as you grow.
              </motion.h2>
            </div>
            <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="text-slate-500 text-base max-w-xs leading-relaxed lg:mb-2">
              No hidden fees. Cancel anytime. Prices in ₹, built for India.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 items-stretch">

            {/* Free */}
            <motion.div
              initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: 0, duration: 0.55 }}
              whileHover={{ y: -5, boxShadow: "0 20px 48px rgba(0,0,0,0.09)", transition: { duration: 0.22 } }}
              className="rounded-3xl bg-white border-2 border-slate-200 p-7 flex flex-col relative overflow-hidden"
              style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}>
              <div className="absolute top-0 right-0 w-36 h-36 rounded-full bg-emerald-50 -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <div className="mb-5">
                <span className="text-[10px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200 px-2.5 py-1 rounded-full">1 MONTH FREE</span>
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-manrope text-5xl font-bold text-slate-900 tracking-tight">₹1,149</span>
                <span className="text-slate-400 text-sm">/mo after</span>
              </div>
              <p className="font-bold text-slate-800 text-lg mb-1">Free</p>
              <p className="text-slate-500 text-xs mb-7 leading-relaxed">Perfect for your first property. No card needed.</p>
              <ul className="space-y-3 flex-1 mb-7">
                {["1 property · 1 iCal feed","20 AI smart replies/mo","Booking calendar & task board","GST-ready P&L reports","Guest Guidebook","Setup Advisor & Legal Hub"].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <CheckCircle size={14} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" />{f}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate("/login")}
                className="w-full py-3 rounded-xl bg-slate-900 hover:bg-slate-700 text-white text-sm font-bold transition-colors">
                Start Free — No Card
              </button>
            </motion.div>

            {/* Starter */}
            <motion.div
              initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: 0.1, duration: 0.55 }}
              whileHover={{ y: -5, boxShadow: "0 20px 48px rgba(59,130,246,0.12)", transition: { duration: 0.22 } }}
              className="rounded-3xl bg-white border-2 border-blue-200 p-7 flex flex-col relative overflow-hidden"
              style={{ boxShadow: "0 4px 16px rgba(59,130,246,0.06)" }}>
              <div className="absolute top-0 right-0 w-36 h-36 rounded-full bg-blue-50 -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <div className="mb-5">
                <span className="text-[10px] font-bold bg-blue-50 text-blue-600 border border-blue-200 px-2.5 py-1 rounded-full">MOST VALUE</span>
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-manrope text-5xl font-bold text-slate-900 tracking-tight">₹3,449</span>
                <span className="text-slate-400 text-sm">/mo</span>
              </div>
              <p className="font-bold text-slate-800 text-lg mb-1">Starter</p>
              <p className="text-slate-500 text-xs mb-7 leading-relaxed">AI pricing that pays for itself in the first week.</p>
              <ul className="space-y-3 flex-1 mb-7">
                {["3 properties · iCal + channel manager","200 AI smart replies/mo","AI Dynamic Pricing","Channel manager (20+ OTAs)","Direct booking link","WhatsApp automations","Guest Guidebook","Property ROI Evaluator"].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <CheckCircle size={14} weight="fill" className="text-blue-500 shrink-0 mt-0.5" />{f}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate("/login")}
                className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-colors">
                Get Starter
              </button>
            </motion.div>

            {/* Pro */}
            <motion.div
              initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: 0.2, duration: 0.55 }}
              whileHover={{ y: -5, transition: { duration: 0.22 } }}
              className="rounded-3xl p-7 flex flex-col relative overflow-hidden"
              style={{
                background: "linear-gradient(145deg, #F59E0B 0%, #FBBF24 55%, #FDE68A 100%)",
                boxShadow: "0 8px 40px rgba(251,191,36,0.30)",
              }}>
              <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/[0.14] pointer-events-none" />
              <div className="absolute -bottom-14 -left-14 w-56 h-56 rounded-full bg-amber-600/15 pointer-events-none" />
              <div className="relative">
                <div className="mb-5">
                  <span className="text-[10px] font-bold bg-slate-900/15 text-slate-900 px-2.5 py-1 rounded-full">MOST POPULAR</span>
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-manrope text-5xl font-bold text-slate-900 tracking-tight">₹11,449</span>
                  <span className="text-amber-900/50 text-sm">/mo</span>
                </div>
                <p className="font-bold text-slate-900 text-lg mb-1">Pro</p>
                <p className="text-amber-900/65 text-xs mb-7 leading-relaxed">Run a real operation — multiple properties, full team.</p>
                <ul className="space-y-3 flex-1 mb-7">
                  {["10 properties · iCal + channel manager","1,000 AI smart replies/mo","Team access (co-host, manager, staff)","Automations & WhatsApp task alerts","Priority support","Everything in Starter"].map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-900">
                      <CheckCircle size={14} weight="fill" className="text-amber-800 shrink-0 mt-0.5" />{f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate("/login")}
                  className="w-full py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold transition-colors"
                  style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.22)" }}>
                  Get Pro
                </button>
              </div>
            </motion.div>

            {/* Agency */}
            <motion.div
              initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.55 }}
              whileHover={{ y: -5, boxShadow: "0 20px 48px rgba(139,92,246,0.10)", transition: { duration: 0.22 } }}
              className="rounded-3xl bg-white border-2 border-violet-200 p-7 flex flex-col relative overflow-hidden"
              style={{ boxShadow: "0 4px 16px rgba(139,92,246,0.06)" }}>
              <div className="absolute top-0 right-0 w-36 h-36 rounded-full bg-violet-50 -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <div className="mb-5">
                <span className="text-[10px] font-bold bg-violet-50 text-violet-600 border border-violet-200 px-2.5 py-1 rounded-full">ENTERPRISE</span>
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-manrope text-5xl font-bold text-slate-900 tracking-tight">Custom</span>
              </div>
              <p className="font-bold text-slate-800 text-lg mb-1">Agency</p>
              <p className="text-slate-500 text-xs mb-7 leading-relaxed">Manage properties for multiple owners at scale.</p>
              <ul className="space-y-3 flex-1 mb-7">
                {[
                  "Unlimited properties",
                  "Unlimited AI smart replies",
                  "White-label branding",
                  "Dedicated account manager",
                  "Consolidated multi-property P&L",
                  "Everything in Pro",
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <CheckCircle size={14} weight="fill" className="text-violet-500 shrink-0 mt-0.5" />{f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => { navigator.clipboard.writeText("support@hostfolio.in").catch(() => {}); setContactCopied(true); setTimeout(() => setContactCopied(false), 3000); }}
                className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold transition-colors">
                {contactCopied ? "✓ Email copied!" : "Get in touch"}
              </button>
            </motion.div>

          </div>

          <p className="text-center text-slate-400 text-xs mt-8">
            All plans include the 1-month free trial &middot; Powered by Razorpay &middot; Cancel anytime
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── FAQ ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <section id="faq" className="py-12 md:py-20 bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-6">
          <div className="mb-8 md:mb-12">
            <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="text-xs font-bold text-amber-500 uppercase tracking-widest mb-4">
              Got questions?
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: 0.06, duration: 0.65 }}
              className="font-manrope font-bold text-slate-900 leading-[0.95]"
              style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
              Frequently<br />Asked Questions
            </motion.h2>
            <p className="text-slate-500 text-sm mt-4">Everything you need to know about HostFolio.</p>
          </div>

          <div className="space-y-3">
            {[
              ["What is HostFolio?","HostFolio is a property management dashboard built for Indian Airbnb hosts. It syncs bookings from Airbnb, Booking.com, MakeMyTrip, Goibibo, VRBO and Agoda, provides AI dynamic pricing, sends guests a digital guidebook via WhatsApp, generates GST-ready P&L reports, automates housekeeping tasks and includes a Legal Resource Hub and Setup Advisor."],
              ["Does it work with Airbnb and Booking.com?","Yes. Paste your iCal link from calendar settings and HostFolio syncs automatically every 15 minutes. Setup takes 30 seconds."],
              ["Does it work with MakeMyTrip and Goibibo?","Yes. HostFolio syncs bookings from MakeMyTrip and Goibibo via iCal, in addition to Airbnb, Booking.com, VRBO and Agoda — one of the few tools that supports Indian OTAs natively."],
              ["What is the Guest Guidebook?","When a booking is confirmed, HostFolio automatically sends the guest a WhatsApp message with a link to their personal digital guidebook — WiFi password, check-in instructions, local restaurants, nearby attractions and emergency contacts. No more answering the same questions over and over."],
              ["What is the Setup Advisor?","A 40-item curated checklist of everything you need to set up a great Airbnb — from mattresses and towels to welcome kits and security cameras. Each item has an INR cost estimate, priority tag (Essential / Recommended / Nice-to-have) and a buy link."],
              ["Does HostFolio have a Legal Resource Hub?","Yes. Plain-English guides on GST registration, police verification, rental agreements, damage recovery and insurance — plus a downloadable rental agreement PDF template."],
              ["How does Smart Pricing work?","HostFolio's AI analyses live competitor prices in your area, your booking history and current occupancy to suggest the optimal nightly rate. Hosts typically see 15–25% more revenue in the first month."],
              ["Does it support GST reporting?","Yes. CGST, SGST and IGST are auto-split based on guest location. Export a clean P&L PDF in one click for your CA. GST rates: 0% under ₹1,000/night, 12% for ₹1,000–7,499, 18% for ₹7,500+."],
              ["How is HostFolio different from other tools?","Most property management tools are built for Western markets at $100–200/month. HostFolio is India-first — INR pricing, GST compliance, WhatsApp automation, UPI payments via Razorpay and AI pricing on Indian market data. Plans from ₹1,149/month, channel manager included."],
              ["How does the task board work?","After every checkout HostFolio auto-creates a cleaning task and sends a WhatsApp alert to your housekeeping staff. Assign tasks, set deadlines and track completion — no manual messages needed."],
              ["What is the Property ROI Evaluator?","An AI calculator for buy vs rent vs lease decisions. Enter a purchase price or rent amount and it returns expected monthly revenue, occupancy, expenses and net ROI using live market data."],
              ["Can I manage multiple properties?","Yes. Free trial supports 1 property. Starter: 3, Pro: 10, Agency: unlimited — all in one dashboard."],
              ["Do you offer a free trial?","Yes — 1 month completely free, no credit card needed. Plans start at ₹1,149/month after that."],
              ["What payment methods are accepted?","UPI, credit/debit cards, net banking and wallets — powered by Razorpay."],
            ].map(([q, a], i) => (
              <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden"
                style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left gap-4 group hover:bg-slate-50 transition-colors">
                  <span className="font-semibold text-slate-800 text-sm group-hover:text-amber-600 transition-colors">{q}</span>
                  <motion.span animate={{ rotate: openFaq === i ? 45 : 0 }} transition={{ duration: 0.2 }}
                    className="text-slate-400 text-xl leading-none shrink-0 font-light">+</motion.span>
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                      <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">{a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          <div className="text-center mt-6">
            <a href="/faq" className="text-sm text-amber-600 hover:text-amber-700 font-semibold transition-colors">
              View all frequently asked questions →
            </a>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── CTA ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <section className="px-6 pb-10 md:pb-14 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden p-10 md:p-16 text-center"
            style={{ background: "linear-gradient(135deg,#F59E0B 0%,#FBBF24 50%,#F97316 100%)", boxShadow: "0 32px 80px rgba(251,191,36,0.30)" }}>
            <div className="absolute inset-0 opacity-[0.06]"
              style={{ backgroundImage: "radial-gradient(circle, white 1.5px, transparent 1.5px)", backgroundSize: "36px 36px" }} />
            <div className="relative">
              <motion.p
                animate={{ opacity: [0.7, 1, 0.7] }} transition={{ duration: 3, repeat: Infinity }}
                className="text-white/80 font-bold text-xs uppercase tracking-widest mb-5">
                Ready to grow?
              </motion.p>
              <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="font-manrope font-bold text-white mb-6 leading-[0.95]"
                style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
                Your first month<br />is completely free.
              </motion.h2>
              <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-white/80 mb-10 text-lg max-w-lg mx-auto leading-relaxed">
                No credit card. No commitment. Just a smarter way to run your rental business in India.
              </motion.p>
              <motion.button onClick={() => navigate("/login")}
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}
                className="bg-white text-amber-600 font-bold px-10 py-4 rounded-full text-base inline-flex items-center gap-2 transition"
                style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
                Start Free Trial <ArrowRight size={18} weight="bold" />
              </motion.button>
              <p className="text-white/55 text-xs mt-5">Setup in 2 minutes · No card needed · Cancel anytime</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 py-14 flex flex-col md:flex-row items-start justify-between gap-10">
          <div>
            <div className="font-manrope text-xl font-bold mb-3">
              <span className="text-slate-900">Host</span><span className="text-amber-500">Folio</span>
            </div>
            <p className="text-sm text-slate-600 max-w-xs leading-relaxed">
              The all-in-one property management dashboard for Airbnb hosts in India. GST-ready, AI-powered, built for ₹.
            </p>
            <div className="flex gap-4 mt-5">
              <a href="https://www.instagram.com/launchwith.hostfolio?igsh=em1yOGFlZDR2eTlu&utm_source=qr" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-700 transition"><InstagramLogo size={20} /></a>
              <a href="https://www.facebook.com/share/1EKewsEmXa/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-700 transition"><FacebookLogo size={20} /></a>
            </div>
          </div>

          <div className="flex flex-wrap gap-12 text-sm text-slate-600">
            <div className="space-y-2.5">
              <p className="font-bold text-slate-800 text-xs uppercase tracking-wide mb-3">Product</p>
              <a href="#how-it-works" className="block hover:text-slate-900 transition">How it works</a>
              <a href="#features"     className="block hover:text-slate-900 transition">Features</a>
              <a href="#pricing"      className="block hover:text-slate-900 transition">Pricing</a>
              <a href="/blog"         className="block hover:text-slate-900 transition">Blog</a>
              <a href="/faq"          className="block hover:text-slate-900 transition">FAQ</a>
              <button onClick={() => navigate("/login")} className="block hover:text-slate-900 transition text-left">Sign In</button>
            </div>
            <div className="space-y-2.5">
              <p className="font-bold text-slate-800 text-xs uppercase tracking-wide mb-3">Legal</p>
              <a href="/privacy" className="block hover:text-slate-900 transition">Privacy Policy</a>
              <a href="/terms"   className="block hover:text-slate-900 transition">Terms of Service</a>
            </div>
            <div className="space-y-2.5">
              <p className="font-bold text-slate-800 text-xs uppercase tracking-wide mb-3">Contact</p>
              <a href="mailto:support@hostfolio.in" className="block hover:text-slate-900 transition">support@hostfolio.in</a>
            </div>
          </div>
        </div>
        <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-500">
          &copy; {new Date().getFullYear()} HostFolio. All rights reserved. &middot; Built for India &#127470;&#127475;
        </div>
      </footer>

    </div>
  );
}
